/**
 * Time-window schedule conflict helpers (EXT.FLEET M2).
 * Works with trip.date + pickup_time / end_time (TIME strings HH:mm:ss).
 */

export interface ScheduleWindow {
  pickupTime: string;
  endTime: string;
}

/** Parse HH:mm or HH:mm:ss to minutes from midnight. */
export function timeStringToMinutes(time: string): number {
  const parts = time.trim().split(':').map(Number);
  const hours = parts[0] ?? 0;
  const minutes = parts[1] ?? 0;
  const seconds = parts[2] ?? 0;
  return hours * 60 + minutes + seconds / 60;
}

export function minutesToTimeString(totalMinutes: number): string {
  const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours = Math.floor(normalized / 60);
  const minutes = Math.floor(normalized % 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
}

export function defaultEndTimeFromPickup(
  pickupTime: string,
  durationMinutes = 120
): string {
  return minutesToTimeString(timeStringToMinutes(pickupTime) + durationMinutes);
}

/** True when windows overlap including buffer after each window's end. */
export function scheduleWindowsOverlap(
  a: ScheduleWindow,
  b: ScheduleWindow,
  bufferMinutes = 15
): boolean {
  const aStart = timeStringToMinutes(a.pickupTime);
  const aEnd = timeStringToMinutes(a.endTime) + bufferMinutes;
  const bStart = timeStringToMinutes(b.pickupTime);
  const bEnd = timeStringToMinutes(b.endTime) + bufferMinutes;
  return aStart < bEnd && bStart < aEnd;
}

/** Whether candidate fits in a gap between two existing windows on the same day. */
export function fitsInIdleGap(
  existing: ScheduleWindow[],
  candidate: ScheduleWindow,
  bufferMinutes = 15
): boolean {
  return !existing.some((block) => scheduleWindowsOverlap(block, candidate, bufferMinutes));
}
